import { Section } from '@/components/containers/section';
import { useTranslation } from '@/contexts/i18n-context';
import { useEncounter } from '@/contexts/encounter-context';
import { useGetProvider } from '@/api/get-provider-info';
import { useGetPharmacyById } from '@/api/get-pharmacy-by-id';
import { Loader } from '@/components/loaders/loader';
import { ProviderListCard } from '@/components/encounter/provider/provider-list-card';
import { PharmacyListCard } from '@/components/encounter/pharmacy/pharmacy-list-card';
import { ReviewEditButton } from '@/components/encounter/review/expandable-info';
import clsx from 'clsx';
import styles from './preferences-section.module.css';
import reviewStyles from '@/styles/review-page.module.css';
import { NextAvailableProviderListCard } from '@/components/encounter/provider/next-available-provider-list-card';
import { Banner } from '@/components/banner';

const translationBase = 'visit.review';

export function PreferencesSection({
  showEditButton = true,
  pharmacySectionRef,
}: {
  showEditButton?: boolean;
  pharmacySectionRef?: React.RefObject<HTMLDivElement | null>;
}) {
  const { t } = useTranslation();
  const { encounter } = useEncounter();
  const {
    data: pharmacy,
    isFetching: isFetchingPharmacy,
    error: errorPharmacy,
  } = useGetPharmacyById(encounter.preferredPharmacyId ?? 0, {
    query: { enabled: !!encounter.preferredPharmacyId },
  });

  return (
    <Section
      className={reviewStyles.section}
      title={t(`${translationBase}.preference.title`)}
      dataTestId="review-section-visit-details"
    >
      <div data-testid="review-visit-details-provider">
        <div className={styles.title}>
          <h3 className={clsx('body-1', 'bold')}>{t(`${translationBase}.preference.provider`)}</h3>
          {showEditButton && (
            <ReviewEditButton
              to={`/visit/${encounter.id}/choose-provider`}
              editModeParam="provider"
              text={t(`${translationBase}.preference.editProvider`)}
            />
          )}
        </div>
        {encounter.preferredProviderId ? (
          <PreferredProvider
            providerId={encounter.preferredProviderId}
            encounterId={encounter.id}
          />
        ) : (
          <NextAvailableProviderListCard />
        )}
      </div>

      <div className={reviewStyles.divider} />

      <div ref={pharmacySectionRef} tabIndex={-1} data-testid="review-visit-details-pharmacy">
        {isFetchingPharmacy ? (
          <Loader type="small" />
        ) : (
          <>
            <div className={styles.title}>
              <h3 className={clsx('body-1', 'bold')}>
                {t(`${translationBase}.preference.pharmacy`)}
              </h3>
              {showEditButton && (
                <ReviewEditButton
                  to={`/visit/${encounter.id}/choose-pharmacy`}
                  editModeParam="pharmacy"
                  text={t(`${translationBase}.preference.editPharmacy`)}
                />
              )}
            </div>
            {pharmacy?.pharmacy && (
              <PharmacyListCard pharmacy={pharmacy.pharmacy} withoutSelectButton />
            )}
            {(errorPharmacy || !encounter.preferredPharmacyId) && (
              <Banner type="error" className={styles.errorBanner} icon="outlined">
                <p>
                  {t(
                    `${translationBase}.preference.errorPharmacy${!showEditButton ? 'ReadOnly' : ''}`,
                  )}
                </p>
              </Banner>
            )}
          </>
        )}
      </div>
    </Section>
  );
}

function PreferredProvider({
  providerId,
  encounterId,
}: {
  providerId: string;
  encounterId: number;
}) {
  const { t } = useTranslation();
  const {
    data: provider,
    isFetching,
    error,
  } = useGetProvider(providerId, {
    encounterId,
  });

  if (isFetching) {
    return <Loader type="small" />;
  }

  if (error || !provider) {
    return <p>{t(`${translationBase}.preference.errorProvider`)}</p>;
  }

  return <ProviderListCard provider={provider.provider} showSelectButton={false} shortView />;
}
